import { z } from 'zod';
import { moneySchema } from '../money';

/**
 * QM-Prozessarten (ADR 0054) — genormte Formate, bewusst fest.
 * NEUENTWICKLUNG/VERBESSERUNG/STANDARD sind Altbestand aus der
 * Base44-Migration (ADR 2026-08-24-base44-altdaten-import): freie
 * Prozessarten ohne genormtes D-Schema, wie im Vorsystem geführt — neu
 * angelegt wird nur noch EIGHT_D/FOUR_D/KVP.
 */
export const qmProcessTypeSchema = z.enum([
  'EIGHT_D',
  'FOUR_D',
  'KVP',
  'NEUENTWICKLUNG',
  'VERBESSERUNG',
  'STANDARD',
]);
export type QmProcessType = z.infer<typeof qmProcessTypeSchema>;

/** Arten, die beim Neuanlegen zur Wahl stehen. NEUENTWICKLUNG/VERBESSERUNG/STANDARD sind Altlasten (s. o.), im Bearbeiten-/Filter-Dropdown aber weiter vollständig sichtbar. */
export const QM_SELECTABLE_PROCESS_TYPES: readonly QmProcessType[] = ['EIGHT_D', 'FOUR_D', 'KVP'];

export const qmProcessStatusSchema = z.enum(['OPEN', 'IN_PROGRESS', 'DONE', 'CANCELLED']);
export type QmProcessStatus = z.infer<typeof qmProcessStatusSchema>;

export const QM_TYPE_LABELS: Record<QmProcessType, string> = {
  EIGHT_D: '8D-Report',
  FOUR_D: '4D-Report',
  KVP: 'KVP',
  NEUENTWICKLUNG: 'Neuentwicklung',
  VERBESSERUNG: 'Verbesserung',
  STANDARD: 'Standard',
};

export const QM_STATUS_LABELS: Record<QmProcessStatus, string> = {
  OPEN: 'Offen',
  IN_PROGRESS: 'In Bearbeitung',
  DONE: 'Abgeschlossen',
  CANCELLED: 'Abgebrochen',
};

/** Die acht D-Felder am Prozess (DB-Spalten); Typen nutzen Teilmengen. */
export const QM_STEP_KEYS = [
  'd1Team',
  'd2Problem',
  'd3Containment',
  'd4RootCause',
  'd5PlannedActions',
  'd6Implementation',
  'd7Prevention',
  'd8Closure',
] as const;
export type QmStepKey = (typeof QM_STEP_KEYS)[number];

export interface QmStep {
  key: QmStepKey;
  label: string;
  /** Hilfetext — im UI als Platzhalter, nicht als Vorbelegung. */
  hint: string;
}

/**
 * Welche D-Schritte ein Prozesstyp zeigt und wie sie heißen (ADR 0054) —
 * zentrale Definition für UI und PDF. 4D nutzt die 8D-Spalten d1/d2/d4/d5
 * unter eigenen Labels; KVP mappt Ist/Vorschlag/Nutzen auf d2/d5/d8.
 */
export const QM_STEPS: Record<QmProcessType, QmStep[]> = {
  EIGHT_D: [
    { key: 'd1Team', label: 'D1 — Team', hint: 'Wer arbeitet an der Lösung? Namen, Rollen, Ansprechpartner.' },
    { key: 'd2Problem', label: 'D2 — Problembeschreibung', hint: 'Was ist passiert? Was / wo / wann / wie viele (Fakten, keine Vermutungen).' },
    { key: 'd3Containment', label: 'D3 — Sofortmaßnahmen', hint: 'Schadensbegrenzung, bis die Ursache behoben ist (inkl. Wirksamkeit).' },
    { key: 'd4RootCause', label: 'D4 — Ursachenanalyse', hint: 'Grundursache(n) — z. B. 5-Why, Ishikawa. Warum ist es passiert, warum wurde es nicht entdeckt?' },
    { key: 'd5PlannedActions', label: 'D5 — Geplante Abstellmaßnahmen', hint: 'Maßnahmen, die die Grundursache dauerhaft beseitigen.' },
    { key: 'd6Implementation', label: 'D6 — Umsetzung & Wirksamkeit', hint: 'Eingeführte Maßnahmen und Wirksamkeitsnachweis.' },
    { key: 'd7Prevention', label: 'D7 — Vorbeugemaßnahmen', hint: 'Übertragung auf ähnliche Produkte/Prozesse, Standards/FMEA aktualisieren.' },
    { key: 'd8Closure', label: 'D8 — Abschluss & Würdigung', hint: 'Team-Erfolg würdigen, Prozess formal abschließen.' },
  ],
  FOUR_D: [
    { key: 'd1Team', label: 'D1 — Team', hint: 'Wer arbeitet an der Lösung?' },
    { key: 'd2Problem', label: 'D2 — Problembeschreibung', hint: 'Was ist passiert? Was / wo / wann / wie viele.' },
    { key: 'd4RootCause', label: 'D3 — Ursachenanalyse', hint: 'Grundursache(n) — z. B. 5-Why.' },
    { key: 'd5PlannedActions', label: 'D4 — Abstellmaßnahmen', hint: 'Maßnahmen inkl. Verantwortlichen und Termin.' },
  ],
  KVP: [
    { key: 'd2Problem', label: 'Ist-Situation', hint: 'Ausgangslage — was läuft heute schlecht/umständlich?' },
    { key: 'd5PlannedActions', label: 'Verbesserungsvorschlag', hint: 'Was soll wie geändert werden?' },
    { key: 'd8Closure', label: 'Nutzen / Ergebnis', hint: 'Erwarteter bzw. erreichter Nutzen (Zeit, Kosten, Qualität).' },
  ],
  // Altbestand aus der Base44-Migration — kein genormtes Schema im Vorsystem,
  // deshalb dieselbe freie KVP-Dreiteilung statt eigener D-Felder.
  NEUENTWICKLUNG: [
    { key: 'd2Problem', label: 'Zusammenfassung', hint: 'Worum geht es?' },
    { key: 'd5PlannedActions', label: 'Geplante Schritte', hint: 'Was ist als Nächstes vorgesehen?' },
    { key: 'd8Closure', label: 'Notizen', hint: 'Weitere Anmerkungen.' },
  ],
  VERBESSERUNG: [
    { key: 'd2Problem', label: 'Zusammenfassung', hint: 'Worum geht es?' },
    { key: 'd5PlannedActions', label: 'Geplante Schritte', hint: 'Was ist als Nächstes vorgesehen?' },
    { key: 'd8Closure', label: 'Notizen', hint: 'Weitere Anmerkungen.' },
  ],
  STANDARD: [
    { key: 'd2Problem', label: 'Zusammenfassung', hint: 'Worum geht es?' },
    { key: 'd5PlannedActions', label: 'Geplante Schritte', hint: 'Was ist als Nächstes vorgesehen?' },
    { key: 'd8Closure', label: 'Notizen', hint: 'Weitere Anmerkungen.' },
  ],
};

const stepFields = {
  d1Team: z.string().trim().max(10000).nullable(),
  d2Problem: z.string().trim().max(10000).nullable(),
  d3Containment: z.string().trim().max(10000).nullable(),
  d4RootCause: z.string().trim().max(10000).nullable(),
  d5PlannedActions: z.string().trim().max(10000).nullable(),
  d6Implementation: z.string().trim().max(10000).nullable(),
  d7Prevention: z.string().trim().max(10000).nullable(),
  d8Closure: z.string().trim().max(10000).nullable(),
};

export const createQmProcessSchema = z.object({
  title: z.string().trim().min(1).max(200),
  type: qmProcessTypeSchema,
  /** Vorlage (optional) — befüllt die D-Felder vor. */
  templateId: z.string().uuid().optional(),
  projectId: z.string().uuid().optional(),
  customerId: z.string().uuid().optional(),
  ownerUserId: z.string().uuid().optional(),
  dueDate: z.string().datetime().optional(),
});
export type CreateQmProcessInput = z.infer<typeof createQmProcessSchema>;

export const updateQmProcessSchema = z
  .object({
    title: z.string().trim().min(1).max(200),
    type: qmProcessTypeSchema,
    status: qmProcessStatusSchema,
    projectId: z.string().uuid().nullable(),
    customerId: z.string().uuid().nullable(),
    ownerUserId: z.string().uuid().nullable(),
    isForMeeting: z.boolean(),
    dueDate: z.string().datetime().nullable(),
    ...stepFields,
  })
  .partial();
export type UpdateQmProcessInput = z.infer<typeof updateQmProcessSchema>;

export const qmProcessResponseSchema = z.object({
  id: z.string().uuid(),
  number: z.string(),
  title: z.string(),
  type: qmProcessTypeSchema,
  status: qmProcessStatusSchema,
  projectId: z.string().uuid().nullable(),
  projectCode: z.string().nullable(),
  customerId: z.string().uuid().nullable(),
  customerName: z.string().nullable(),
  ownerUserId: z.string().uuid().nullable(),
  ownerName: z.string().nullable(),
  isForMeeting: z.boolean(),
  dueDate: z.string().datetime().nullable(),
  d1Team: z.string().nullable(),
  d2Problem: z.string().nullable(),
  d3Containment: z.string().nullable(),
  d4RootCause: z.string().nullable(),
  d5PlannedActions: z.string().nullable(),
  d6Implementation: z.string().nullable(),
  d7Prevention: z.string().nullable(),
  d8Closure: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type QmProcessResponse = z.infer<typeof qmProcessResponseSchema>;

// ── Kosten & Zeiten ─────────────────────────────────────────────────────────

export const createQmCostEntrySchema = z.object({
  description: z.string().trim().min(1).max(500),
  amount: moneySchema,
  incurredAt: z.string().datetime(),
});
export type CreateQmCostEntryInput = z.infer<typeof createQmCostEntrySchema>;

export const qmCostEntryResponseSchema = z.object({
  id: z.string().uuid(),
  processId: z.string().uuid(),
  description: z.string(),
  amount: moneySchema,
  incurredAt: z.string().datetime(),
  createdAt: z.string().datetime(),
});
export type QmCostEntryResponse = z.infer<typeof qmCostEntryResponseSchema>;

export const createQmTimeEntrySchema = z.object({
  description: z.string().trim().min(1).max(500),
  person: z.string().trim().min(1).max(200),
  hours: z.number().gt(0).max(10000),
  workedAt: z.string().datetime(),
});
export type CreateQmTimeEntryInput = z.infer<typeof createQmTimeEntrySchema>;

export const qmTimeEntryResponseSchema = z.object({
  id: z.string().uuid(),
  processId: z.string().uuid(),
  description: z.string(),
  person: z.string(),
  hours: z.number(),
  workedAt: z.string().datetime(),
  createdAt: z.string().datetime(),
});
export type QmTimeEntryResponse = z.infer<typeof qmTimeEntryResponseSchema>;

// ── Vorlagen ────────────────────────────────────────────────────────────────

export const qmProcessTemplateResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  type: qmProcessTypeSchema,
  position: z.number().int(),
  d1Team: z.string().nullable(),
  d2Problem: z.string().nullable(),
  d3Containment: z.string().nullable(),
  d4RootCause: z.string().nullable(),
  d5PlannedActions: z.string().nullable(),
  d6Implementation: z.string().nullable(),
  d7Prevention: z.string().nullable(),
  d8Closure: z.string().nullable(),
});
export type QmProcessTemplateResponse = z.infer<typeof qmProcessTemplateResponseSchema>;
